export interface WikiArticle {
  title: string;
  extract: string;
  fullUrl: string;
  pageId: number;
  categories: string[];
  thumbnail?: string;
  description?: string;
  timestamp: string;
}

/**
 * Stored article summary with reading metadata
 */
export interface StoredArticleSummary {
  // Basic info
  title: string;
  url: string;
  pageId: number;

  // Metadata
  description?: string;
  categories: string[];
  thumbnail?: string;

  // Reading stats
  readAt: string; // ISO datetime when article was marked as read
  startedAt: string; // ISO datetime when article was first loaded
  timeSpentSeconds: number; // Time spent reading in seconds
}

export interface ArticleHistory {
  articles: StoredArticleSummary[];
  totalRead: number;
  milestonesAchieved: number[];
  lastArticle?: WikiArticle; // Full last article for resuming (converted from API response)
}

const WIKIPEDIA_API = "https://en.wikipedia.org/w/api.php";

/**
 * Fetches a random Wikipedia article with metadata
 */
export async function fetchRandomArticle(): Promise<WikiArticle> {
  // First, get a random article title
  const randomUrl = new URL(WIKIPEDIA_API);
  randomUrl.searchParams.set("action", "query");
  randomUrl.searchParams.set("format", "json");
  randomUrl.searchParams.set("origin", "*");
  randomUrl.searchParams.set("list", "random");
  randomUrl.searchParams.set("rnnamespace", "0"); // Main namespace only
  randomUrl.searchParams.set("rnlimit", "1");

  const randomResponse = await fetch(randomUrl.toString());
  const randomData = await randomResponse.json();
  const pageTitle = randomData.query.random[0].title;
  const pageId = randomData.query.random[0].id;

  // Now get full article details with full extract (not just intro)
  // Use exsectionformat=wiki to get section markers like == Heading ==
  const detailsUrl = new URL(WIKIPEDIA_API);
  detailsUrl.searchParams.set("action", "query");
  detailsUrl.searchParams.set("format", "json");
  detailsUrl.searchParams.set("origin", "*");
  detailsUrl.searchParams.set("titles", pageTitle);
  detailsUrl.searchParams.set(
    "prop",
    "extracts|info|categories|pageimages|description",
  );
  detailsUrl.searchParams.set("explaintext", "true");
  detailsUrl.searchParams.set("exsectionformat", "wiki"); // Use wiki format to get == markers for headings
  detailsUrl.searchParams.set("inprop", "url");
  detailsUrl.searchParams.set("cllimit", "5");
  detailsUrl.searchParams.set("piprop", "thumbnail");
  detailsUrl.searchParams.set("pithumbsize", "400");

  const detailsResponse = await fetch(detailsUrl.toString());
  const detailsData = await detailsResponse.json();

  const page = Object.values(detailsData.query.pages)[0] as {
    title: string;
    extract: string;
    fullurl: string;
    pageid: number;
    categories?: Array<{ title: string }>;
    thumbnail?: { source: string };
    description?: string;
  };

  const categories = (page.categories || [])
    .map((cat) => cat.title.replace("Category:", ""))
    .slice(0, 5);

  return {
    title: page.title,
    extract: page.extract || "No extract available for this article.",
    fullUrl: page.fullurl,
    pageId: page.pageid || pageId,
    categories,
    thumbnail: page.thumbnail?.source,
    description: page.description,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Fetches a Wikipedia article by its page ID
 * Used for loading articles from history
 */
export async function fetchArticleByPageId(
  pageId: number,
): Promise<WikiArticle> {
  const detailsUrl = new URL(WIKIPEDIA_API);
  detailsUrl.searchParams.set("action", "query");
  detailsUrl.searchParams.set("format", "json");
  detailsUrl.searchParams.set("origin", "*");
  detailsUrl.searchParams.set("pageids", pageId.toString());
  detailsUrl.searchParams.set(
    "prop",
    "extracts|info|categories|pageimages|description",
  );
  detailsUrl.searchParams.set("explaintext", "true");
  detailsUrl.searchParams.set("exsectionformat", "wiki");
  detailsUrl.searchParams.set("inprop", "url");
  detailsUrl.searchParams.set("cllimit", "5");
  detailsUrl.searchParams.set("piprop", "thumbnail");
  detailsUrl.searchParams.set("pithumbsize", "400");

  const detailsResponse = await fetch(detailsUrl.toString());
  const detailsData = await detailsResponse.json();

  const page = Object.values(detailsData.query.pages)[0] as {
    title: string;
    extract: string;
    fullurl: string;
    pageid: number;
    categories?: Array<{ title: string }>;
    thumbnail?: { source: string };
    description?: string;
  };

  const categories = (page.categories || [])
    .map((cat) => cat.title.replace("Category:", ""))
    .slice(0, 5);

  return {
    title: page.title,
    extract: page.extract || "No extract available for this article.",
    fullUrl: page.fullurl,
    pageId: page.pageid,
    categories,
    thumbnail: page.thumbnail?.source,
    description: page.description,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Milestone definitions
 */
export const MILESTONES = [
  {
    id: 1,
    count: 5,
    name: "Curious Cat 🐱",
    description:
      "You've read 5 articles! Curiosity is the beginning of wisdom.",
    icon: "🌟",
  },
  {
    id: 2,
    count: 15,
    name: "Knowledge Seeker 📚",
    description: "15 articles down! You're building a foundation of knowledge.",
    icon: "🏆",
  },
  {
    id: 3,
    count: 30,
    name: "Wisdom Walker 🚶",
    description: "30 articles! You're walking the path of enlightenment.",
    icon: "💎",
  },
  {
    id: 4,
    count: 50,
    name: "Scholar Supreme 🎓",
    description: "50 articles! You're officially a trivia master!",
    icon: "👑",
  },
  {
    id: 5,
    count: 100,
    name: "Encyclopedia Legend 🏅",
    description: "100 articles! You've achieved legendary status!",
    icon: "🌈",
  },
];

/**
 * Get milestone progress percentage
 */
export function getMilestoneProgress(totalRead: number): number {
  const nextMilestone = MILESTONES.find((m) => m.count > totalRead);
  if (!nextMilestone) return 100;

  const prevMilestone = MILESTONES.filter((m) => m.count <= totalRead).pop();
  const prevCount = prevMilestone?.count || 0;

  const progress =
    ((totalRead - prevCount) / (nextMilestone.count - prevCount)) * 100;
  return Math.min(progress, 100);
}

/**
 * Get the current milestone level
 */
export function getCurrentMilestoneLevel(totalRead: number): number {
  const achieved = MILESTONES.filter((m) => m.count <= totalRead);
  return achieved.length;
}
